import { getType, subTypes, types } from "./itemTypes";
import getQuestion from "./getQuestion";
import getChoiceOption from "./Choice/getChoiceOption";
import getDescription from "../UserTimeline/TimelineItem/getDescription";
import detailsModel from "./detailsModel";
import { logging } from "../tools";

// Translate Stijn's question object to new format
function getItem(question, num) {
  let newQuestion = {};
  switch (question.typeQuestion) {
    case subTypes.open:
      newQuestion = getQuestion(subTypes.open, num);
      break;
    case subTypes.multipleChoice:
      const getMCType = () => {
        if (question.itemExtra.optionVals) return subTypes.likert;
        if (question.itemExtra.allowMultipleChoice)
          return subTypes.multipleChoice;
        return subTypes.singleChoice;
      };
      newQuestion = {
        ...getQuestion(getMCType(), num),
        options: getMultipleChoiceOptions(question, "options"),
      };
      break;

    case subTypes.likert:
      newQuestion = getQuestion("Old likert", num); // unsupported question
      break;

    case subTypes.order:
      newQuestion = {
        ...getQuestion(subTypes.order, num),
        options: getMultipleChoiceOptions(question, "options"),
      };
      break;

    case subTypes.multiSmiley:
      newQuestion = getQuestion(subTypes.multiSmiley, num);
      break;

    case subTypes.bodyParts:
      newQuestion = getQuestion(subTypes.bodyParts, num);
      break;

    case subTypes.yesno:
      newQuestion = getQuestion(subTypes.yesno, num);
      break;

    case subTypes.imageChoice:
      newQuestion = {
        ...getQuestion(subTypes.multipleChoice, num),
        options: getMultipleChoiceOptions(question, "imageLocations"),
        type: question.itemExtra.allowMultipleChoice
          ? subTypes.multipleChoice
          : subTypes.singleChoice,
        hasImages: true,
      };
      break;

    case subTypes.sliderNeutralPos:
    case subTypes.sliderNegPos:
      newQuestion = {
        ...getQuestion(subTypes.slider, num),
        marks: getSliderMarks(question),
      };
      break;

    case subTypes.smiley:
      newQuestion = {
        ...getQuestion(subTypes.smiley, num),
        typeSmiley: question.itemExtra.typeSmiley,
      };
      break;

    case subTypes.container:
      newQuestion = getQuestion(subTypes.container, num);
      break;

    case subTypes.computation:
      newQuestion = {
        ...getQuestion(subTypes.computation, num),
        expressionString: question.itemExtra.expressionString,
        isText: false,
      };
      break;

    case subTypes.text:
      newQuestion = getQuestion(subTypes.text, num);
      break;

    default: // unsupported question
      newQuestion = getQuestion(question.typeQuestion, num);
      break;
  }
  const fullQuestion = question?.fullQuestion ?? "";
  //const dom = new DOMParser().parseFromString(fullQuestion, "text/html");
  //const title = dom.body.textContent;
  const title = fullQuestion;
  const checked =
    question?.itemExtra?.hide === undefined
      ? true
      : !Boolean(question.itemExtra.hide); // curate

  const label = question?.shortQuestion ?? "";
  return {
    ...newQuestion,
    label,
    title,
    checked,
    original: question,
  };
}

function getMultipleChoiceOptions(question, mode) {
  let options = [];
  question?.itemExtra?.options?.map((mc, index) =>
    options.push(
      getChoiceOption({ title: mc, number: index + 1 + "", img: "" })
    )
  );
  switch (mode) {
    case "options":
      options.forEach(
        (option, index) =>
          (option.number = question.itemExtra?.optionVals?.[index] ?? index + 1)
      );
      break;

    case "imageLocations":
      options.forEach((option, index) => {
        option.img = question.itemExtra.imageLocations[index] ?? "";
      });
      break;

    default:
      break;
  }
  return options;
}

function getSliderMarks(question) {
  const defaultSliderMarks = getQuestion(subTypes.slider).marks;
  const sliderNegPosMarks = getQuestion(subTypes.sliderNegPos).marks;
  const sliderNeutralPosMarks = getQuestion(subTypes.sliderNeutralPos).marks;
  let marks = defaultSliderMarks; // default marks: no special labels
  if (question.typeQuestion === subTypes.sliderNegPos)
    marks = sliderNegPosMarks; // sliderNegPos defaults
  if (question.typeQuestion === subTypes.sliderNeutralPos)
    marks = sliderNeutralPosMarks; // sliderNeutralPos defaults
  // custom marks
  if (question.itemExtra.minInt) marks[0].value = question.itemExtra.minInt;
  if (question.itemExtra.anchorLeft) {
    marks[0].type = "custom";
    marks[0].label = question.itemExtra.anchorLeft;
  }
  if (question.itemExtra.maxInt) marks[1].value = question.itemExtra.maxInt;
  if (question.itemExtra.anchorRight) {
    marks[1].type = "custom";
    marks[1].label = question.itemExtra.anchorRight;
  }
  return marks;
}

function getCondition(child, parent) {
  const primaryCond = child.condition[parent.original.typeQuestion];
  switch (getType(parent.type)) {
    case types.choice:
      if ([subTypes.yesno, subTypes.computation].includes(parent.type)) {
        if (primaryCond.length > 1) {
          logging("LEGACY PROBLEM. Import empty condition");
          return [];
        }
        return parent.options
          .filter((_, index) => primaryCond.includes(index))
          .map((option) => option.id);
      }
      if ([subTypes.multiSmiley, subTypes.bodyParts].includes(parent.type)) {
        return parent.options
          .filter((option) => primaryCond.includes(option.type))
          .map((option) => option.id);
      }
      return parent.options
        .filter((_, index) => primaryCond.includes(index + 1))
        .map((option) => option.id);

    case types.slider:
      const marks = child.condition[parent.original.typeQuestion];
      return [{ value: marks[0] }, { value: marks[1] }];

    default:
      return [];
  }
}

export default function importQuestions(questionList) {
  let { items, ...other } = questionList;
  const buildTree = (items, parent, number) => {
    const sNum = number ?? "";
    return items?.map((item, i) => {
      const num = sNum + (i + 1) + ".";
      // item defaults
      let node = getItem(item, num);
      // conditional
      if (parent && item.condition?.[parent.original.typeQuestion]?.length > 0) {
        node.conditions = getCondition(item, parent);
      }
      if (item.defaultValue?.[item.typeQuestion] !== undefined) {
        node.defaultValue = item.defaultValue[item.typeQuestion];
      }
      // children
      if (item?.items)
        node.items = buildTree(
          item.items, // children
          node,
          num
        );
      return node;
    });
  };
  const questions = buildTree(items, null);
  const description = getDescription(other?.itemExtra); // long description of survey
  const details = detailsModel(other);

  return { questions, description, details };
}
